import { DeliveryType, ShippingEstimate, ShippingOptionDelivery } from '@pedix-workspace/utils';
import { ShippingCostRequest, ShippingCostResponse } from './delivery.types';
import { fetchShippingEstimates } from './fetch-delivery-estimates';
import { getDeliveryCostByZone } from './delivery-zones.utils';

export async function calculateDeliveryCost(
  shippingOptionDelivery: ShippingOptionDelivery,
  request: ShippingCostRequest,
): Promise<ShippingCostResponse | null> {
  const { totalAmountWithDiscount, address, hasLogisticsIntegration } = request;

  if (shippingOptionDelivery.deliveryOperation === 'OWN') {
    return calculateOwnDeliveryCost(shippingOptionDelivery, totalAmountWithDiscount, address);
  }

  if (shippingOptionDelivery.deliveryOperation === 'INTEGRATION') {
    return calculateIntegrationDeliveryCost(
      shippingOptionDelivery,
      request,
      hasLogisticsIntegration,
    );
  }

  return null;
}

function calculateOwnDeliveryCost(
  shippingOption: ShippingOptionDelivery,
  totalAmount: number,
  address?: ShippingCostRequest['address'],
): ShippingCostResponse | null {
  const { deliveryType, minimumPurchase, minimumPurchaseForFreeShipping, deliveryCost } =
    shippingOption;

  if (deliveryType === DeliveryType.TO_BE_DEFINED || deliveryType === DeliveryType.FIXED) {
    if (isMinimumPurchaseNotMet(totalAmount, minimumPurchase)) {
      return createMinimumPurchaseErrorResponse(minimumPurchase, shippingOption);
    }
    if (isFreeShippingGranted(totalAmount, minimumPurchaseForFreeShipping)) {
      return createFreeShippingResponse(minimumPurchaseForFreeShipping, shippingOption);
    }
  }

  switch (deliveryType) {
    case DeliveryType.TO_BE_DEFINED:
      return createToBeDefined(shippingOption);
    case DeliveryType.FIXED:
      return createFixedCostResponse(deliveryCost, shippingOption);
    case DeliveryType.DELIVERY_ZONES:
      const result = calculateZoneBasedDeliveryCost(address, shippingOption, totalAmount);

      return result;
    default:
      return null;
  }
}

async function calculateIntegrationDeliveryCost(
  shippingOption: ShippingOptionDelivery,
  request: ShippingCostRequest,
  hasLogisticsIntegration: boolean,
): Promise<ShippingCostResponse | null> {
  const { totalAmountWithDiscount, address } = request;
  const { minimumPurchase, minimumPurchaseForFreeShipping } = shippingOption;

  if (!address?.addressDetails) {
    // Not enough information to calculate shipping cost
    return null;
  }

  if (!hasLogisticsIntegration) {
    return createOutOfDeliveryZoneResponse(shippingOption);
  }

  if (isMinimumPurchaseNotMet(totalAmountWithDiscount, minimumPurchase)) {
    return createMinimumPurchaseErrorResponse(minimumPurchase, shippingOption);
  }

  // Fetch estimates first
  const shippingEstimate: ShippingEstimate = request.previousShippingEstimate
    ? request.previousShippingEstimate
    : await fetchShippingEstimates(request.apiBaseUrl, request.establishment, {
        cartItems: request.cartItems,
        personalInformation: {
          name: request.name,
          phone: request.phone,
          address: request.address,
        },
      }).then(shippingEstimates => shippingEstimates?.[0]);

  if (!shippingEstimate) {
    return createOutOfDeliveryZoneResponse(shippingOption);
  }

  const isFree = isFreeShippingGranted(totalAmountWithDiscount, minimumPurchaseForFreeShipping);

  return createShippingEstimateResponse(
    shippingEstimate,
    shippingOption,
    isFree,
    minimumPurchaseForFreeShipping,
  );
}

function calculateZoneBasedDeliveryCost(
  address: ShippingCostRequest['address'] | undefined,
  shippingOption: ShippingOptionDelivery,
  totalAmount: number,
): ShippingCostResponse | null {
  if (!address?.addressDetails) return null;

  const matchingZone = getDeliveryCostByZone(
    address.addressDetails.coordinates,
    shippingOption.deliveryZones,
  );

  if (!matchingZone) return createOutOfDeliveryZoneResponse(shippingOption);

  if (isMinimumPurchaseNotMet(totalAmount, matchingZone.minimumPurchase)) {
    return createMinimumPurchaseErrorResponse(matchingZone.minimumPurchase, shippingOption);
  }

  if (isFreeShippingGranted(totalAmount, matchingZone.minimumPurchaseForFreeShipping)) {
    return createFreeShippingResponse(matchingZone.minimumPurchaseForFreeShipping, shippingOption);
  }

  return createDeliveryCostResponse(
    matchingZone.cost,
    matchingZone.minimumPurchaseForFreeShipping,
    shippingOption,
  );
}

const isMinimumPurchaseNotMet = (total: number, minimum?: number | null) =>
  minimum ? total < minimum : false;
const isFreeShippingGranted = (total: number, minimum?: number | null) =>
  minimum ? total >= minimum : false;

// Response creators
const createMinimumPurchaseErrorResponse = (
  amount: number,
  shippingOption: ShippingOptionDelivery,
): ShippingCostResponse => ({
  minimumPurchaseErrorAmount: amount,
  shippingOption,
});

const createFreeShippingResponse = (
  amount: number,
  shippingOption: ShippingOptionDelivery,
): ShippingCostResponse => ({
  freeShippingGrantedAmount: amount,
  shippingOption,
});

const createToBeDefined = (shippingOption: ShippingOptionDelivery): ShippingCostResponse => ({
  deliveryCostToBeDefined: true,
  minimumPurchaseForFreeShipping: shippingOption.minimumPurchaseForFreeShipping,
  shippingOption,
});

const createFixedCostResponse = (
  cost: number | undefined,
  shippingOption: ShippingOptionDelivery,
): ShippingCostResponse => ({
  deliveryCost: cost || 0,
  minimumPurchaseForFreeShipping: shippingOption.minimumPurchaseForFreeShipping,
  shippingOption,
});

const createOutOfDeliveryZoneResponse = (
  shippingOption: ShippingOptionDelivery,
): ShippingCostResponse => ({
  outOfDeliveryZone: true,
  shippingOption,
});

const createShippingEstimateResponse = (
  estimate: any,
  shippingOption: ShippingOptionDelivery,
  isFree: boolean,
  freeShippingMinimum?: number,
): ShippingCostResponse => ({
  deliveryCost: isFree ? 0 : estimate.price,
  minimumPurchaseForFreeShipping: freeShippingMinimum,
  outOfDeliveryZone: estimate.outOfDeliveryZone || false,
  shippingEstimate: estimate,
  shippingOption,
  ...(isFree && { freeShippingGrantedAmount: freeShippingMinimum }),
});

const createDeliveryCostResponse = (
  cost: number | undefined,
  freeShippingMinimum: number | undefined,
  shippingOption: ShippingOptionDelivery,
): ShippingCostResponse => ({
  deliveryCost: cost || 0,
  minimumPurchaseForFreeShipping: freeShippingMinimum,
  shippingOption,
});
